import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { MenuItemList, MenuItems } from "../NavMenuMobile/NavMenuMobile";
import { BiztripLogo } from "~/components/BiztripLogo";
import { Box } from "~/components/Box";
import { Col } from "~/components/Grid";
import { Text } from "~/components/Text";
import { SvgAnalytics, SvgClose, SvgMetric, SvgPerson } from "~/components/Icon/icons";
import { Actions, AgencyEmployeeType, UserContext } from "~/application/types";
import { routes } from "~/application/theme/routes";
import { IconButton } from "~/components/IconButton";
import { filterMenuItems, NavMenuModule } from "~/application/utils/menu-functions";
import {
  expensesMenuItems,
  notificationsMenuItems,
  orderMenuItems,
  searchMenuItems,
  settingsMenuItems,
} from "~/application/data";
import { useUser } from "~/presentation/core/contexts/UserContext";
import { Icon } from "~/components/Icon";
import { Flex } from "~/components/Flex";
import { useVerifyActions } from "~/presentation/shared/hooks/useVerifyActions";
import { DataListItemMenu } from "~/components/List/DataListItem/DataListItemMenu";
import { customerService } from "~/application/usecases";
import { QueryTimes } from "~/constants";
import { logError } from "~/presentation/shared/utils/errors";
import { AdditionalInfo } from "~/core/modules/Agency/pages/AgencyOrderPage/components/AdditionalInfoDialog";
import { dialogService } from "~/components/DialogStack";
import { CustomerInfoDialog } from "~/core/modules/Agency/pages/AgencyOrderPage/components/CustomerInfoDialog";
import { AdditionalInfoProps } from "~/core/modules/Agency/pages/AgencyOrderPage";
import { Skeleton } from "~/components/Skeleton";
import { getServiceIcon } from "~/presentation/shared/utils";

const LOAD_ERROR_MESSAGE = "Erro ao carregar informações do cliente";

const LOG_TAG = "NavMenu/SideNavMenu";
export interface SideNavMenuProps {
  onNavigate: (path: string) => void;
  onCloseMenu: () => void;
}

export const SideNavMenu = ({ onCloseMenu, onNavigate }: SideNavMenuProps) => {
  const { user, userActions, profile, contexts } = useUser();

  const activeMenu = useMemo<NavMenuModule>(() => {
    if (location.pathname.startsWith("/busca")) return "search";
    if (location.pathname.startsWith("/pedidos")) return "order";
    if (location.pathname.startsWith("/despesas/solicitar-adiantamento")) return "search";
    if (location.pathname.startsWith("/despesas/lancar-despesas")) return "search";
    if (location.pathname.startsWith("/despesas")) return "expenses";
    if (location.pathname.startsWith("/configuracoes")) return "settings";
    if (location.pathname.startsWith("/notificacoes")) return "notifications";
    if (location.pathname.startsWith("/dashboard-bi")) return "dashboard";
    if (location.pathname.startsWith("/central-de-relatorios")) return "reporting-center";
  }, [location.pathname]);

  const [searchIsOpen, setSearchIsOpen] = useState(activeMenu === "search");
  const [orderIsOpen, setOrderIsOpen] = useState(activeMenu === "order");
  const [expanseIsOpen, setExpanseIsOpen] = useState(activeMenu === "expenses");
  const [configIsOpen, setConfigIsOpen] = useState(activeMenu === "settings");
  const [notificationsIsOpen, setNotificationsIsOpen] = useState(activeMenu === "notifications");

  const { data: additionalInfo, isLoading: isLoadingAdditionalInfo } = useQuery(
    [contexts.customer],
    async () =>
      await customerService.findById(contexts?.customer.uuid ?? "", {
        withApprovers: true,
        withIssuers: true,
        withAdditionalInfo: true,
      }),
    {
      staleTime: QueryTimes.SMALLEST,
      refetchOnMount: "always",
      enabled: !!contexts?.customer,
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: LOAD_ERROR_MESSAGE,
        });
      },
    },
  );

  const dialogAdditionalInfo = useCallback(({ label, obs }: AdditionalInfoProps) => {
    return dialogService.showDialog(<AdditionalInfo label={label} obs={obs} />);
  }, []);

  const customerInfoDialog = useCallback(() => {
    return dialogService.showDialog(
      <CustomerInfoDialog
        approvers={additionalInfo?.approvers}
        issuers={additionalInfo?.issuers}
      />,
    );
  }, [additionalInfo]);

  const searchMenu = useMemo(() => {
    const items = filterMenuItems(searchMenuItems, userActions, contexts);

    if (items.length === 0) return;

    return (
      <Box>
        <MenuItemList
          data-active={activeMenu === "search"}
          isOpen={searchIsOpen}
          onItemExpand={() => setSearchIsOpen((old) => !old)}
          item={{ type: MenuItems.SEARCH }}
        >
          <Flex direction="column" css={{ pl: "$16", pr: "$4" }}>
            {items.map((item) => (
              <Text
                key={item.title}
                as="p"
                fw="500"
                css={{
                  p: "$4",
                  borderRadius: "$md",
                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor: "$primary-light",
                  },
                }}
                onClick={() => onNavigate(item.href)}
              >
                {item.title}
              </Text>
            ))}
          </Flex>
        </MenuItemList>
      </Box>
    );
  }, [activeMenu, userActions, contexts, searchIsOpen, setSearchIsOpen, onNavigate]);

  const orderMenu = useMemo(() => {
    const items = filterMenuItems(orderMenuItems, userActions, contexts, user);

    if (items.length === 0) return;

    return (
      <Box>
        <MenuItemList
          data-active={activeMenu === "order"}
          isOpen={orderIsOpen}
          onItemExpand={() => setOrderIsOpen((old) => !old)}
          item={{ type: MenuItems.ORDER }}
        >
          <Flex direction="column" css={{ pl: "$16", pr: "$4" }}>
            {items.map((item) => (
              <Text
                key={item.title}
                as="p"
                fw="500"
                css={{
                  p: "$4",
                  borderRadius: "$md",
                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor: "$primary-light",
                  },
                }}
                onClick={() => onNavigate(item.href)}
              >
                {item.title}
              </Text>
            ))}
          </Flex>
        </MenuItemList>
      </Box>
    );
  }, [activeMenu, userActions, contexts, user, orderIsOpen, setOrderIsOpen, onNavigate]);

  const expenseMenu = useMemo(() => {
    const items = filterMenuItems(expensesMenuItems, userActions, contexts);

    if (items.length === 0 || profile?.isExpenseApprover) return;

    return (
      <Box>
        <MenuItemList
          data-active={activeMenu === "expenses"}
          isOpen={expanseIsOpen}
          onItemExpand={() => setExpanseIsOpen((old) => !old)}
          item={{ type: MenuItems.EXPENSES }}
        >
          <Flex direction="column" css={{ pl: "$16", pr: "$4" }}>
            {items.map((item) => (
              <Text
                key={item.title}
                as="p"
                fw="500"
                css={{
                  p: "$4",
                  borderRadius: "$md",
                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor: "$primary-light",
                  },
                }}
                onClick={() => onNavigate(item.href)}
              >
                {item.title}
              </Text>
            ))}
          </Flex>
        </MenuItemList>
      </Box>
    );
  }, [activeMenu, userActions, contexts, profile, expanseIsOpen, setExpanseIsOpen, onNavigate]);

  const settingsMenu = useMemo(() => {
    const items = filterMenuItems(settingsMenuItems, userActions, contexts);

    const isManager = user.type === AgencyEmployeeType.MANAGER;
    const isValidAgencyEmployee = isManager && user.context === UserContext.Agency;
    const isValidCustomerEmployee = user.context === UserContext.Customer;

    if (items.length && (isValidAgencyEmployee || isValidCustomerEmployee)) {
      return (
        <Box>
          <MenuItemList
            data-active={activeMenu === "settings"}
            isOpen={configIsOpen}
            onItemExpand={() => setConfigIsOpen((old) => !old)}
            item={{ type: MenuItems.SETTINGS }}
          >
            <Flex direction="column" css={{ pl: "$16", pr: "$4" }}>
              {items.map((item) => (
                <Text
                  key={item.title}
                  as="p"
                  fw="500"
                  css={{
                    p: "$4",
                    borderRadius: "$md",
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "$primary-light",
                    },
                  }}
                  onClick={() => onNavigate(item.href)}
                >
                  {item.title}
                </Text>
              ))}
            </Flex>
          </MenuItemList>
        </Box>
      );
    }
  }, [activeMenu, userActions, contexts, user, configIsOpen, setConfigIsOpen, onNavigate]);

  const notificationsMenu = useMemo(() => {
    const isBiztripUser = user.context === UserContext.Biztrip;
    if (!isBiztripUser) return;

    const items = filterMenuItems(notificationsMenuItems, userActions, contexts);
    if (items.length === 0) return;

    return (
      <Box>
        <MenuItemList
          data-active={activeMenu === "notifications"}
          isOpen={notificationsIsOpen}
          onItemExpand={() => setNotificationsIsOpen((old) => !old)}
          item={{ type: MenuItems.NOTIFICATIONS }}
        >
          <Flex direction="column" css={{ pl: "$16", pr: "$4" }}>
            {items.map((item) => (
              <Text
                key={item.title}
                as="p"
                fw="500"
                css={{
                  p: "$4",
                  borderRadius: "$md",
                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor: "$primary-light",
                  },
                }}
                onClick={() => onNavigate(item.href)}
              >
                {item.title}
              </Text>
            ))}
          </Flex>
        </MenuItemList>
      </Box>
    );
  }, [
    activeMenu,
    userActions,
    contexts,
    user,
    notificationsIsOpen,
    setNotificationsIsOpen,
    onNavigate,
  ]);

  const reportingCenterMenu = useMemo(() => {
    const canShowReportingCenter = useVerifyActions({
      actions: [Actions.ViewPurchaseReport],
      contexts,
      profile,
    });

    if (!canShowReportingCenter || !contexts?.customer) return;

    return (
      <DataListItemMenu
        data-active={activeMenu === "reporting-center"}
        onClick={() => onNavigate(routes.ReportingCenter.root)}
        css={{
          "&:hover": {
            cursor: "pointer",
            backgroundColor: "$primary-light",
          },
        }}
      >
        <Icon as={SvgMetric} variant="darkest" size="md" />
        <Col>
          <Text size="4" css={{ fw: "600" }}>
            Central de relatório
          </Text>
        </Col>
      </DataListItemMenu>
    );
  }, [activeMenu, userActions, contexts, onNavigate]);

  const dashboardMenu = useMemo(() => {
    const canShowDashboard = useVerifyActions({
      actions: [Actions.ViewDashboard],
      contexts,
      profile,
    });

    if (!canShowDashboard) return;

    return (
      <DataListItemMenu
        data-active={activeMenu === "dashboard"}
        onClick={() => onNavigate(routes.DashboardBIV2.root)}
        css={{
          "&:hover": {
            cursor: "pointer",
            backgroundColor: "$primary-light",
          },
        }}
      >
        <Icon as={SvgAnalytics} variant="darkest" size="md" />
        <Col>
          <Text size="4" css={{ fw: "600" }}>
            Dashboard
          </Text>
        </Col>
      </DataListItemMenu>
    );
  }, [activeMenu, contexts, profile, onNavigate]);

  const crmMenu = useMemo(() => {
    const isManager = user.type === AgencyEmployeeType.MANAGER;
    const isValidAgencyEmployee = isManager && user.context === UserContext.Agency;

    if (isValidAgencyEmployee && contexts.customer) {
      return !isLoadingAdditionalInfo ? (
        <Flex direction="column">
          <DataListItemMenu
            onClick={customerInfoDialog}
            css={{
              "&:hover": {
                cursor: "pointer",
                backgroundColor: "$primary-light",
              },
            }}
          >
            <Icon as={SvgPerson} variant="darkest" size="md" />
            <Col>
              <Text size="4" css={{ fw: "600" }}>
                Informações do cliente
              </Text>
            </Col>
          </DataListItemMenu>

          {Object.entries(additionalInfo?.additionalInfo || []).map(([key, value]) => (
            <DataListItemMenu
              key={key}
              onClick={() => dialogAdditionalInfo({ label: key, obs: value.obs })}
              css={{
                "&:hover": {
                  cursor: "pointer",
                  backgroundColor: "$primary-light",
                },
              }}
            >
              <Icon as={getServiceIcon(key)} variant="darkest" size="md" />
              <Col>
                <Text size="4" css={{ fw: "600" }}>
                  {key}
                </Text>
              </Col>
            </DataListItemMenu>
          ))}
        </Flex>
      ) : (
        <Flex align="center">
          <Skeleton css={{ width: "200px", height: "25px" }} />
        </Flex>
      );
    }

    return null;
  }, [activeMenu, userActions, contexts, user, additionalInfo, isLoadingAdditionalInfo]);

  return (
    <Box
      css={{
        "@mxxl": {
          width: "80%",
          overflow: "auto",
          height: "100vh",
          position: "absolute",
          transition: "ease-in-out",
          zIndex: "$overlay",
          top: "0",
          left: "0",
          backgroundColor: "$neutrals-white",
        },
      }}
    >
      <Flex
        align="center"
        justify="between"
        css={{
          padding: "$10",
          height: "$20",
        }}
      >
        <BiztripLogo size="lg" />

        <IconButton>
          <SvgClose onClick={onCloseMenu} />
        </IconButton>
      </Flex>

      {user.context === UserContext.Biztrip ? (
        notificationsMenu
      ) : (
        <>
          {searchMenu}
          {orderMenu}
          {expenseMenu}
          {settingsMenu}
          {reportingCenterMenu}
          {dashboardMenu}
          {crmMenu}
        </>
      )}
    </Box>
  );
};
